import axios from 'axios'
import { sessionIdentity, invalidateSessionCache } from '@utils/employerSessionCache'
import { removeLocalStorage } from '@utils/localStorage'

const pickedApiRequest = axios.create({
  baseURL: process.env.VUE_APP_PICKED_API_ENDPOINT
})

const pickedApiCandidateRequest = axios.create({
  baseURL: process.env.VUE_APP_PICKED_API_ENDPOINT
})

pickedApiRequest.interceptors.request.use((config) => {
  config.employerIdentity = sessionIdentity()
  const method = (config.method || 'get').toLowerCase()
  // Report visibility records telemetry only; it must not discard list filters.
  const resultObservation = method === 'post' && /^assessment\/organisations\/[^/?#]+\/result-opened$/.test(config.url || '')
  if (!['get', 'head', 'options'].includes(method) && !resultObservation) invalidateSessionCache()
  if (localStorage.getItem('employer.token')) {
    const bearer = 'Bearer ' + localStorage.getItem('employer.token')
    config['headers']['Authorization'] = bearer
  }
  if (localStorage.getItem('employer.ghost')) {
    config['headers']['X-Ghost-User'] = localStorage.getItem('employer.ghost')
  }
  if (localStorage.getItem('employer.childOrganisationId')) {
    config['headers']['X-Organisation-ID'] = localStorage.getItem('employer.childOrganisationId')
  }
  return config
})

// A token the server no longer accepts (the account was confirmed on another
// device, or removed) ends the session here rather than leaving every screen
// to fail one request at a time. Public auth routes are left alone.
const AUTH_ROUTE = /\/(sign-in|sign-up|join|reset)(\/|$)/
pickedApiRequest.interceptors.response.use(response => {
  if (response.config.employerIdentity !== sessionIdentity()) return Promise.reject(new Error('Workspace changed. Refresh to continue.'))
  return response
}, error => {
  if (error.config && error.config.employerIdentity !== sessionIdentity()) return Promise.reject(error)
  const status = error.response && error.response.status
  if (status === 401 || status === 403) invalidateSessionCache()
  const url = (error.config && error.config.url) || ''
  const publicAuthCall = /assessment\/(employers\/(email-status|sign-up|magic-link|google|microsoft|google-email|microsoft-email|provider-sign-up|[^/]+\/(auth-links|auth-requests|token)\/)|join\/)/.test(url)
  if (status === 401 && !publicAuthCall && localStorage.getItem('employer.token') && !AUTH_ROUTE.test(window.location.pathname)) {
    removeLocalStorage('employer.token')
    removeLocalStorage('employer.childOrganisationId')
    window.location.assign('/sign-in?session=ended')
  }
  return Promise.reject(error)
})

pickedApiCandidateRequest.interceptors.request.use((config) => {
  if (localStorage.getItem('candidateWizard.uuid')) {
    config['auth'] = {
      username: localStorage.getItem('candidateWizard.uuid'),
      password: localStorage.getItem('candidateWizard.password')
    }
  }
  return config
})

// An expired wizard credential is refused with 401 wizard_credential_expired.
// Forget it here so the route guards send the candidate to sign in, and mark
// the error so screens can say "sign in again" rather than "check your connection".
pickedApiCandidateRequest.interceptors.response.use(response => response, error => {
  const data = error.response && error.response.data
  if (error.response && error.response.status === 401 && data && data.error === 'wizard_credential_expired') {
    error.wizardCredentialExpired = true
    removeLocalStorage('candidateWizard.uuid')
    removeLocalStorage('candidateWizard.password')
  }
  return Promise.reject(error)
})

export { pickedApiRequest, pickedApiCandidateRequest }
